// @boty/i18n - Locale configuration for next-intl
import { getRequestConfig } from 'next-intl/server';

/** Supported locales in the application */
export const locales = ['en', 'he', 'es'] as const;

/** Union type of supported locale codes */
export type Locale = (typeof locales)[number];

/** Default locale (English) used when no locale is specified */
export const defaultLocale: Locale = 'en';

/** URL prefix strategy: always show /en/ or /he/ in URL */
export const localePrefix = 'always' as const;

/**
 * Type guard to check if a string is a valid locale
 * @param locale - The string to check
 * @returns True if the string is a valid Locale ('en' or 'he')
 * @example
 * isValidLocale('en') // true
 * isValidLocale('fr') // false
 */
export function isValidLocale(locale: string): locale is Locale {
  return locales.includes(locale as Locale);
}

/**
 * Get the text direction for a given locale
 * @param locale - The locale to get direction for
 * @returns 'rtl' for Hebrew, 'ltr' for all other locales
 * @example
 * getDirection('he') // 'rtl'
 * getDirection('en') // 'ltr'
 */
export function getDirection(locale: Locale): 'ltr' | 'rtl' {
  return locale === 'he' ? 'rtl' : 'ltr';
}

/** Pre-configured middleware settings for next-intl */
export const middlewareConfig = {
  locales,
  defaultLocale,
  localePrefix,
};

// Request config for next-intl/server
// Apps should use this as the default export in their i18n.ts file
export default getRequestConfig(async ({ requestLocale }) => {
  // Get locale from request, falling back to default
  const requested = await requestLocale;
  const locale = isValidLocale(requested ?? '') ? requested! : defaultLocale;

  // Load all namespace messages for the locale
  const [common, consumer, creator, admin] = await Promise.all([
    import(`../locales/${locale}/common.json`).then((m) => m.default),
    import(`../locales/${locale}/consumer.json`).then((m) => m.default),
    import(`../locales/${locale}/creator.json`).then((m) => m.default),
    import(`../locales/${locale}/admin.json`).then((m) => m.default),
  ]);

  return {
    locale,
    messages: {
      common,
      consumer,
      creator,
      admin,
    },
  };
});
